import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody, EmptyState } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const today = () => new Date().toISOString().slice(0, 10);

export default function Timesheet() {
  const { t, lang } = useI18n();
  const { data: entries = [] } = useSWR("/timesheets", fetcher);
  const { data: projects = [] } = useSWR("/projects", fetcher);
  const { data: employees = [] } = useSWR("/employees", fetcher);
  const activeEmps = employees.filter(e => e.active);
  const [form, setForm] = useState({ project_id: "", worker_name: "", date: today(), hours: 8, hourly_rate: 35, notes: "" });

  const onEmpChange = (name) => {
    const e = employees.find(x => x.name === name);
    setForm({ ...form, worker_name: name, hourly_rate: e?.hourly_rate ?? form.hourly_rate });
  };

  const submit = async () => {
    if (!form.worker_name || !form.hours) return toast.error(lang === "fr" ? "Travailleur et heures requis" : "Worker and hours required");
    try {
      await api.post("/timesheets", { ...form, project_id: form.project_id || null });
      toast.success(lang === "fr" ? "Heures enregistrées" : "Hours logged");
      setForm({ ...form, hours: 8, notes: "" });
      mutate("/timesheets");
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/timesheets/${id}`);
    mutate("/timesheets");
  };

  const totalHours = entries.reduce((a, b) => a + Number(b.hours || 0), 0);
  const totalPay = entries.reduce((a, b) => a + Number(b.hours || 0) * Number(b.hourly_rate || 0), 0);
  const projectName = (id) => projects.find(p => p.id === id)?.name || "—";

  return (
    <div data-testid="timesheet-page">
      <PageHeader eyebrow="// 05 / Timesheet" title={t("timesheet.title")} sub={t("timesheet.sub")} />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 border border-zinc-200 h-fit">
            <div className="border-b border-zinc-200 px-5 py-3"><p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Saisie" : "Log entry"}</p></div>
            <div className="p-5 space-y-3">
              <Field label={t("timesheet.worker")}>
                {activeEmps.length > 0 ? (
                  <Select value={form.worker_name} onValueChange={onEmpChange}>
                    <SelectTrigger data-testid="ts-worker" className="rounded-none"><SelectValue placeholder={lang === "fr" ? "Choisir…" : "Pick…"} /></SelectTrigger>
                    <SelectContent>{activeEmps.map(e => <SelectItem key={e.id} value={e.name}>{e.name} · ${e.hourly_rate}/h</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input data-testid="ts-worker" value={form.worker_name} onChange={(e) => setForm({ ...form, worker_name: e.target.value })} className="rounded-none" placeholder={lang === "fr" ? "Ajoutez d'abord un employé" : "Add employees first"} />
                )}
              </Field>
              <Field label={t("nav.projects")}>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="ts-project" className="rounded-none"><SelectValue placeholder={lang === "fr" ? "(optionnel)" : "(optional)"} /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("common.date")}><Input data-testid="ts-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none mono" /></Field>
                <Field label={t("timesheet.hours")}><Input data-testid="ts-hours" type="number" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} className="rounded-none mono" /></Field>
              </div>
              <Field label={lang === "fr" ? "Taux ($/h)" : "Rate ($/hr)"}><Input data-testid="ts-rate" type="number" step="0.25" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: Number(e.target.value) })} className="rounded-none mono" /></Field>
              <Field label={t("common.notes")}><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" /></Field>
              <Button data-testid="ts-submit" onClick={submit} className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider w-full h-11 text-xs">
                <Plus size={14} className="mr-2" /> {t("timesheet.log")}
              </Button>
            </div>
          </div>

          <div className="lg:col-span-2 border border-zinc-200">
            <div className="border-b border-zinc-200 px-5 py-3 flex justify-between items-center">
              <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Entrées récentes" : "Recent entries"}</p>
              <div className="flex gap-6 mono text-xs">
                <span className="text-zinc-500">{t("common.total")}: <strong className="text-zinc-900">{totalHours}h</strong></span>
                <span className="text-zinc-500">{t("timesheet.cost")}: <strong className="text-orange-600">${totalPay.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></span>
              </div>
            </div>
            {entries.length === 0 ? (
              <div className="p-10"><EmptyState title={lang === "fr" ? "Aucune heure enregistrée" : "No hours logged yet"} sub={t("timesheet.sub")} /></div>
            ) : (
              <div>
                <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
                  <div className="col-span-2">{t("common.date")}</div>
                  <div className="col-span-3">{t("timesheet.worker")}</div>
                  <div className="col-span-3">{t("nav.projects")}</div>
                  <div className="col-span-1 text-right">{t("timesheet.hours")}</div>
                  <div className="col-span-2 text-right">$/h</div>
                  <div className="col-span-1"></div>
                </div>
                {entries.map(e => (
                  <div key={e.id} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center text-sm">
                    <div className="col-span-2 mono text-xs">{e.date}</div>
                    <div className="col-span-3 font-semibold">{e.worker_name}</div>
                    <div className="col-span-3 text-zinc-600">{projectName(e.project_id)}</div>
                    <div className="col-span-1 text-right mono">{e.hours}</div>
                    <div className="col-span-2 text-right mono">${e.hourly_rate}</div>
                    <div className="col-span-1 text-right">
                      <button data-testid={`ts-delete-${e.id}`} onClick={() => remove(e.id)} className="text-zinc-400 hover:text-orange-600"><Trash size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
